import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, ArrowRight, Loader as Loader2, Check, Clock, History } from 'lucide-react';
import { firebaseService } from '../services/firebaseService';
import { auth } from '../services/firebase';

interface CareerTransitionFormProps {
  onRoadmapGenerated: (subject: string, goal: string) => void;
}

interface Topic {
  name: string;
  description: string;
  transferableSkills?: string[];
}

interface TopicsResponse {
  message: string;
  topics: Topic[];
}

export function CareerTransitionForm({ onRoadmapGenerated }: CareerTransitionFormProps) {
  const [currentRole, setCurrentRole] = useState('');
  const [desiredRole, setDesiredRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [topics, setTopics] = useState<Topic[]>([]);
  const [inspiringMessage, setInspiringMessage] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [previousTransitions, setPreviousTransitions] = useState<any[]>([]);

  useEffect(() => {
    const loadPreviousTransitions = async () => {
      if (auth.currentUser) {
        try {
          const transitions = await userProgressService.getCareerTransitions(auth.currentUser.uid);
          setPreviousTransitions(transitions);
        } catch (error) {
          console.error('Error loading previous transitions:', error);
        }
      }
    };

    loadPreviousTransitions();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentRole.trim() || !desiredRole.trim()) return;

    setLoading(true);
    setError('');
    setTopics([]);

    try {
      // Generate learning topics first
      const response = await perplexityService.generateTopics(currentRole, desiredRole);
      setInspiringMessage(response.message);
      
      // Save to Firebase
      if (auth.currentUser) {
        await userProgressService.saveCareerTransition(auth.currentUser.uid, {
          currentRole,
          desiredRole,
          topics: response.topics,
          message: response.message
        });
        
        // Refresh previous transitions
        const transitions = await userProgressService.getCareerTransitions(auth.currentUser.uid);
        setPreviousTransitions(transitions);
      }
      
      setTopics(response.topics);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to generate learning topics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSelect = (topicName: string) => {
    setSelectedTopic(topicName);
    const subject = `${topicName} for ${desiredRole}`;
    const goal = `Master ${topicName} to transition from ${currentRole} to ${desiredRole}`;
    onRoadmapGenerated(subject, goal);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-6 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50 opacity-50" />
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Career Transition Planner</h2>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Role
              </label>
              <input
                type="text"
                value={currentRole}
                onChange={(e) => setCurrentRole(e.target.value)}
                placeholder="e.g., Marketing Manager"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Desired Role
              </label>
              <input
                type="text"
                value={desiredRole}
                onChange={(e) => setDesiredRole(e.target.value)}
                placeholder="e.g., Product Manager"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !currentRole.trim() || !desiredRole.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing Career Path...</span>
                </>
              ) : (
                <>
                  <span>Analyze Transition Path</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {topics.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 space-y-6"
            >
              {inspiringMessage && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-blue-800 font-medium">{inspiringMessage}</p>
                </div>
              )}

              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Recommended Learning Topics
              </h3>
              <div className="space-y-3">
                {topics.map((topic, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 border rounded-lg transition-colors duration-200 ${
                      selectedTopic === topic.name
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handleTopicSelect(topic.name)}
                        className="flex-1 text-left"
                      >
                        <h4 className="font-medium text-gray-900 mb-1">{topic.name}</h4>
                        <p className="text-sm text-gray-600">{topic.description}</p>
                        {topic.transferableSkills && topic.transferableSkills.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-gray-700">Relevant skills you already have:</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {topic.transferableSkills.map((skill, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </button>
                      {selectedTopic === topic.name && (
                        <div className="flex-shrink-0 bg-blue-100 p-1 rounded-full">
                          <Check className="w-4 h-4 text-blue-600" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
        
        {previousTransitions.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 border-t border-gray-200 pt-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              Previous Transitions
            </h3>
            <div className="space-y-3">
              {previousTransitions.map((transition, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(transition.timestamp).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-gray-900">
                    <span>{transition.currentRole}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <span>{transition.desiredRole}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {transition.topics.map((topic: Topic, i: number) => (
                      <button
                        key={i}
                        onClick={() => {
                          setCurrentRole(transition.currentRole);
                          setDesiredRole(transition.desiredRole);
                          setTopics(transition.topics);
                          handleTopicSelect(topic.name);
                        }}
                        className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200"
                      >
                        {topic.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}